import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CreditCard, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      try {
        // Get the payment_intent_client_secret from URL
        const clientSecret = searchParams.get('payment_intent_client_secret');
        if (!clientSecret) {
          throw new Error('No payment intent client secret found');
        }

        // Confirm the payment with Stripe
        const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

        if (error) {
          throw new Error(error.message);
        }

        if (!paymentIntent) {
          throw new Error('No payment intent found');
        }

        if (paymentIntent.status === 'succeeded') {
          // Invalidate credits query to reload the user's credit information
          queryClient.invalidateQueries({ queryKey: ['credits'] });

          // Show success toast
          toast({
            title: 'Payment Successful',
            description: 'Your subscription has been activated',
          });

          setError(null);
        } else {
          throw new Error(`Payment status: ${paymentIntent.status}`);
        }
      } catch (error) {
        console.error('Payment confirmation error:', error);
        setError(error instanceof Error ? error.message : 'Failed to confirm payment');
        toast({
          title: 'Error',
          description: 'Failed to confirm payment. Please contact support.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    handlePaymentSuccess();
  }, [queryClient, toast, searchParams]);

  if (isLoading) {
    return (
      <div className="container max-w-md py-16">
        <div className="flex flex-col items-center text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-md py-16">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold mb-3">Payment Failed</h1>
          <p className="text-muted-foreground mb-8 max-w-sm">
            {error}
          </p>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate('/pricing')}>
              Try Again
            </Button>
            <Button onClick={() => window.location.href = "mailto:support@example.com"}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-md py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center text-center"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <Check className="h-8 w-8 text-green-600" />
        </div>

        <h1 className="text-3xl font-bold mb-3">Payment Successful!</h1>
        <p className="text-muted-foreground mb-8 max-w-sm">
          Thank you for your purchase. Your subscription is now active and your credits have been added to your account.
        </p>

        <div className="bg-muted p-4 rounded-lg w-full mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Status</span>
            <span className="text-sm text-green-600 font-medium">Active</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Credits</span>
            <span className="text-sm font-medium flex items-center">
              <CreditCard className="h-3.5 w-3.5 mr-1.5" />
              Available in your account
            </span>
          </div>
        </div>

        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate('/profile')}>
            View Subscription
          </Button>
          <Button onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      </motion.div>
    </div>
  );
} 